import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import SectionLabel from "../components/SectionLabel";
import { getAnimals, submitVolunteer } from "../lib/api";

const INTERESTS = ["Rescue rides", "Foster a pet", "Vet assistance", "Walking & feeding", "Photography", "Social media"];

export default function Adopt() {
  const [animals, setAnimals] = useState([]);
  const [vol, setVol] = useState({ name: "", email: "", phone: "", city: "", message: "", interests: [] });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { getAnimals().then(setAnimals).catch(() => {}); }, []);

  const updateVol = (k, v) => setVol((s) => ({ ...s, [k]: v }));

  const toggleInterest = (i) => {
    setVol((s) => ({
      ...s,
      interests: s.interests.includes(i) ? s.interests.filter((x) => x !== i) : [...s.interests, i],
    }));
  };

  const handleVolunteer = async (e) => {
    e.preventDefault();
    if (!vol.name || !vol.email || !vol.phone || !vol.city) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await submitVolunteer(vol);
      setSubmitted(true);
      toast.success("Welcome to the pack — we'll be in touch soon!");
      setVol({ name: "", email: "", phone: "", city: "", message: "", interests: [] });
    } catch {
      toast.error("Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="adopt-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="max-w-3xl space-y-6 fade-up">
            <SectionLabel>Adopt · Foster · Volunteer</SectionLabel>
            <h1 className="font-serif text-5xl lg:text-7xl font-light text-forest leading-[0.95]">
              Bring home a <em className="italic text-terracotta">soulmate</em> with four paws.
            </h1>
            <p className="text-lg text-ink/80 leading-relaxed">
              Every animal here has been medically cleared, behaviourally rehabilitated and is ready for a real home. We screen carefully — because once they leave us, they should never have to come back.
            </p>
          </div>
        </div>
      </section>

      {/* BENTO GRID */}
      <section className="pb-24" data-testid="animals-grid-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6">
            {animals.map((a, i) => {
              // Bento layout: vary spans
              const layouts = [
                "lg:col-span-5 lg:row-span-2 aspect-[4/5]",
                "lg:col-span-7 aspect-[16/10]",
                "lg:col-span-4 aspect-[3/4]",
                "lg:col-span-3 aspect-[3/4]",
                "lg:col-span-5 aspect-[16/10]",
                "lg:col-span-7 aspect-[16/9]",
              ];
              const cls = layouts[i % layouts.length];
              return (
                <article
                  key={a.id}
                  className={`relative group rounded-3xl overflow-hidden bg-white border border-bone-soft ${cls}`}
                  data-testid={`animal-card-${a.id}`}
                >
                  <img src={a.image} alt={a.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/85 via-forest/20 to-transparent"/>
                  <div className="absolute top-4 left-4 bg-bone/95 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold text-forest flex items-center gap-1.5">
                    <MapPin className="w-3 h-3" strokeWidth={1.8}/> {a.location}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-bone">
                    <div className="flex items-baseline gap-3 mb-2">
                      <h3 className="font-serif text-3xl lg:text-4xl">{a.name}</h3>
                      <span className="text-xs text-bone/80 uppercase tracking-wider">{a.age} · {a.gender}</span>
                    </div>
                    <p className="text-sm text-bone/85 line-clamp-2 max-w-md mb-3">{a.story}</p>
                    <div className="flex gap-2 flex-wrap">
                      {a.tags.map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-wider bg-bone/15 backdrop-blur px-2.5 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* VOLUNTEER FORM */}
      <section className="py-24 bg-forest text-bone" data-testid="volunteer-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-honey">
              <span className="w-8 h-px bg-honey" /> Join the pack
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light leading-tight">
              Volunteer your <em className="italic text-honey">time, hands or skills.</em>
            </h2>
            <p className="text-bone/85 leading-relaxed">
              We need driving rescues, weekend feeders, photographers, social media wizards, and hugs. Pick what fits — we'll find a way.
            </p>
          </div>

          <form onSubmit={handleVolunteer} className="lg:col-span-7 bg-bone rounded-[2rem] p-8 lg:p-10 space-y-5 text-ink" data-testid="volunteer-form">
            {submitted ? (
              <div className="text-center py-12 space-y-4" data-testid="volunteer-success">
                <div className="w-16 h-16 rounded-full bg-terracotta/10 flex items-center justify-center mx-auto">
                  <span className="text-3xl">🐾</span>
                </div>
                <h3 className="font-serif text-3xl text-forest">You're in.</h3>
                <p className="text-ink/70 max-w-sm mx-auto">We'll email you within 48 hours with onboarding details and the next sanctuary visit date.</p>
                <Button onClick={() => setSubmitted(false)} variant="outline" className="border-forest text-forest rounded-full">Submit another</Button>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vol-name" className="text-sm text-ink/70 mb-2 block">Full name *</Label>
                    <Input id="vol-name" required value={vol.name} onChange={(e) => updateVol("name", e.target.value)} className="h-12 rounded-xl" data-testid="volunteer-name-input"/>
                  </div>
                  <div>
                    <Label htmlFor="vol-email" className="text-sm text-ink/70 mb-2 block">Email *</Label>
                    <Input id="vol-email" type="email" required value={vol.email} onChange={(e) => updateVol("email", e.target.value)} className="h-12 rounded-xl" data-testid="volunteer-email-input"/>
                  </div>
                  <div>
                    <Label htmlFor="vol-phone" className="text-sm text-ink/70 mb-2 block">Phone *</Label>
                    <Input id="vol-phone" required value={vol.phone} onChange={(e) => updateVol("phone", e.target.value)} className="h-12 rounded-xl" data-testid="volunteer-phone-input"/>
                  </div>
                  <div>
                    <Label htmlFor="vol-city" className="text-sm text-ink/70 mb-2 block">City *</Label>
                    <Input id="vol-city" required value={vol.city} onChange={(e) => updateVol("city", e.target.value)} className="h-12 rounded-xl" data-testid="volunteer-city-input"/>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-ink/70 mb-2 block">How can you help?</Label>
                  <div className="flex flex-wrap gap-2">
                    {INTERESTS.map((i) => {
                      const active = vol.interests.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleInterest(i)}
                          className={`px-4 py-2 rounded-full text-sm border transition-all ${
                            active ? "bg-terracotta text-bone border-terracotta" : "border-bone-soft text-ink hover:border-terracotta"
                          }`}
                          data-testid={`volunteer-interest-${i.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        >
                          {i}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="vol-msg" className="text-sm text-ink/70 mb-2 block">Anything else? (optional)</Label>
                  <Textarea id="vol-msg" rows={3} value={vol.message} onChange={(e) => updateVol("message", e.target.value)} className="rounded-xl" data-testid="volunteer-message-input"/>
                </div>

                <Button type="submit" disabled={loading} className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-13 px-8 py-3.5 font-semibold w-full md:w-auto" data-testid="volunteer-submit-button">
                  {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Submitting...</>) : "Sign me up"}
                </Button>
              </>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
